import { useEffect, useState } from "react";
import { Link as ScrollLink } from "react-scroll";
import CountUp from "react-countup";
import {
  FaStar,
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaSwimmingPool,
  FaFire,
  FaUtensils,
  FaVolleyballBall,
  FaWifi,
  FaCar,
  FaMusic,
  FaSpa,
  FaArrowUp,
  FaBars,
  FaTimes,
} from "react-icons/fa";

// ---------------------------
// Data
// ---------------------------
const farmhouses = [
  {
    name: "Royal Palm Estate",
    img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?q=80&w=2070",
    price: "₹45,000/night",
    rating: 4.9,
    guests: "12 Guests • 6 Bedrooms",
    features: ["Private Pool", "Luxury Spa", "Bonfire Area", "BBQ Zone"],
  },
  {
    name: "Lakeview Paradise",
    img: "https://images.unsplash.com/photo-1507089947368-19c1da9775ae?q=80&w=2070",
    price: "₹38,000/night",
    rating: 4.8,
    guests: "10 Guests • 5 Bedrooms",
    features: ["Lake View", "Fishing Dock", "Kayaking", "Sunset Deck"],
  },
  {
    name: "Emerald Greens",
    img: "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&w=2070",
    price: "₹52,000/night",
    rating: 5.0,
    guests: "15 Guests • 7 Bedrooms",
    features: ["Golf Course", "Wine Cellar", "Home Theater", "Chef Kitchen"],
  },
  {
    name: "Mountain Retreat",
    img: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=2065",
    price: "₹42,000/night",
    rating: 4.7,
    guests: "8 Guests • 4 Bedrooms",
    features: ["Mountain View", "Hiking Trails", "Hot Tub", "Fireplace"],
  },
  {
    name: "Sunset Vineyard",
    img: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?q=80&w=2070",
    price: "₹65,000/night",
    rating: 4.9,
    guests: "20 Guests • 8 Bedrooms",
    features: ["Wine Tasting", "Vineyard Tour", "Banquet Hall", "Pool Bar"],
  },
  {
    name: "Riverfront Haven",
    img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2070",
    price: "₹35,000/night",
    rating: 4.6,
    guests: "6 Guests • 3 Bedrooms",
    features: ["River Access", "Floating Deck", "Water Sports", "Picnic Area"],
  },
];

const amenities = [
  { icon: <FaSwimmingPool />, title: "Infinity Pools", desc: "Temperature-controlled luxury pools" },
  { icon: <FaFire />, title: "Bonfire Nights", desc: "Cozy fire pits with seating" },
  { icon: <FaUtensils />, title: "Gourmet Dining", desc: "Private chefs & farm-to-table meals" },
  { icon: <FaVolleyballBall />, title: "Sports Arena", desc: "Badminton, volleyball & more" },
  { icon: <FaWifi />, title: "High-Speed WiFi", desc: "Seamless connectivity throughout" },
  { icon: <FaCar />, title: "Valet Parking", desc: "Secure parking for all guests" },
  { icon: <FaMusic />, title: "Sound Systems", desc: "Premium indoor/outdoor audio" },
  { icon: <FaSpa />, title: "Wellness Spa", desc: "Ayurvedic treatments & massages" },
];

const testimonials = [
  {
    name: "Rajesh Mehta",
    role: "Wedding Planner",
    text: "Organized a destination wedding for 150 guests. Flawless execution and stunning venues!",
    rating: 5,
  },
  {
    name: "Priya Sharma",
    role: "Corporate Event Manager",
    text: "Our team retreat was transformative. The amenities and service exceeded expectations.",
    rating: 5,
  },
  {
    name: "Arun Patel",
    role: "Family Vacationer",
    text: "Three generations celebrated together. Memories created will last a lifetime.",
    rating: 4.8,
  },
];

// ---------------------------
// Main App Component
// ---------------------------
export default function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 300);

      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="bg-dark text-white">
      {/* ========== NAVBAR ========== */}
      <nav className={`navbar navbar-expand-lg fixed-top ${isScrolled ? "bg-dark bg-opacity-95 border-bottom border-danger" : "bg-transparent"}`}>
        <div className="container">
          <a className="navbar-brand d-flex align-items-center gap-2" href="#">
            <img src="/logo.png" alt="Logo" className="img-fluid" style={{ width: "60px", height: "60px" }} />
            <h1 className="h4 mb-0 fw-bold text-gradient">V Farm House</h1>
          </a>

          <button
            className="navbar-toggler border-0 text-white"
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>

          <div className={`collapse navbar-collapse ${isMobileMenuOpen ? "show" : ""}`}>
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4">
              {["home", "farmhouses", "amenities", "testimonials", "contact"].map((item) => (
                <li className="nav-item" key={item}>
                  <ScrollLink
                    to={item}
                    smooth
                    duration={600}
                    className="nav-link text-white-50 hover-text-danger px-2"
                    onClick={closeMobileMenu}
                  >
                    {item.toUpperCase()}
                  </ScrollLink>
                </li>
              ))}
            </ul>
            <ScrollLink to="contact" smooth duration={600} className="btn btn-danger rounded-pill px-4">
              <FaWhatsapp className="me-2" /> Book Now
            </ScrollLink>
          </div>
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section id="home" className="min-vh-100 d-flex align-items-center text-center position-relative overflow-hidden pt-5">
        <div className="container position-relative z-1">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-danger small tracking-wide mb-4">LUXURY NATURE RETREATS</div>
              <h1 className="display-3 fw-bold">
                Where Luxury <span className="text-gradient">Meets Nature</span>
              </h1>
              <p className="lead text-white-50 mx-auto mt-4" style={{ maxWidth: "700px" }}>
                Experience unparalleled luxury in our handpicked farmhouses, designed for
                unforgettable celebrations, peaceful retreats, and magical moments in nature's lap.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mt-5">
                <button className="btn btn-danger rounded-pill px-5 py-3 fw-semibold">Explore Properties</button>
                <button className="btn btn-outline-danger rounded-pill px-5 py-3 fw-semibold">Virtual Tour</button>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Cards */}
        <div className="container mt-5 pt-4">
          <div className="row g-4">
            {[
              { icon: "🎯", title: "Custom Events", desc: "Tailored to your needs" },
              { icon: "🛡️", title: "24/7 Security", desc: "Complete privacy assured" },
              { icon: "👨‍🍳", title: "Private Chef", desc: "Gourmet dining experience" },
              { icon: "🚁", title: "Helipad Access", desc: "Seamless transportation" },
            ].map((item, i) => (
              <div className="col-sm-6 col-lg-3" key={i}>
                <div className="card bg-white bg-opacity-10 border-white border-opacity-10 rounded-4 text-center h-100">
                  <div className="card-body p-4">
                    <div className="display-4 mb-3">{item.icon}</div>
                    <h5 className="fw-bold">{item.title}</h5>
                    <p className="text-white-50 small">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STATS COUNTER ========== */}
      <section className="py-5">
        <div className="container">
          <div className="row text-center g-4">
            <div className="col-6 col-lg-3">
              <h2 className="display-4 fw-bold text-danger">
                <CountUp end={500} duration={4} />+
              </h2>
              <p className="text-white-50 small text-uppercase">Happy Guests</p>
            </div>
            <div className="col-6 col-lg-3">
              <h2 className="display-4 fw-bold text-danger">
                <CountUp end={50} duration={4} />+
              </h2>
              <p className="text-white-50 small text-uppercase">Luxury Properties</p>
            </div>
            <div className="col-6 col-lg-3">
              <h2 className="display-4 fw-bold text-danger">
                <CountUp end={4.9} decimals={1} duration={4} />
              </h2>
              <p className="text-white-50 small text-uppercase">Guest Rating</p>
            </div>
            <div className="col-6 col-lg-3">
              <h2 className="display-4 fw-bold text-danger">
                <CountUp end={100} duration={4} />+
              </h2>
              <p className="text-white-50 small text-uppercase">Events Hosted</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FARMHOUSES SECTION ========== */}
      <section id="farmhouses" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <div className="text-danger small tracking-wide mb-2">EXCLUSIVE COLLECTION</div>
            <h2 className="display-5 fw-bold">Featured Farmhouses</h2>
            <p className="text-white-50 mx-auto mt-3" style={{ maxWidth: "600px" }}>
              Discover our curated selection of luxury properties, each offering unique experiences and premium amenities.
            </p>
          </div>

          <div className="row g-4">
            {farmhouses.map((house, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <div className="card bg-dark border-white border-opacity-10 rounded-4 overflow-hidden h-100">
                  <div className="position-relative">
                    <img src={house.img} className="card-img-top" alt={house.name} style={{ height: "250px", objectFit: "cover" }} />
                    <div className="position-absolute top-0 end-0 m-3">
                      <span className="badge bg-danger px-3 py-2 rounded-pill">{house.price}</span>
                    </div>
                  </div>
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="fw-bold mb-0">{house.name}</h5>
                      <div className="text-warning">
                        <FaStar className="me-1" /> {house.rating}
                      </div>
                    </div>
                    <p className="text-white-50 small mb-3">{house.guests}</p>
                    <div className="d-flex flex-wrap gap-2 mb-4">
                      {house.features.map((feature, idx) => (
                        <span key={idx} className="badge bg-danger bg-opacity-25 text-danger px-3 py-2 rounded-pill small">
                          {feature}
                        </span>
                      ))}
                    </div>
                    <button className="btn btn-danger w-100 rounded-pill">View Details</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== AMENITIES SECTION ========== */}
      <section id="amenities" className="py-5 bg-black bg-opacity-25">
        <div className="container">
          <div className="text-center mb-5">
            <div className="text-danger small tracking-wide mb-2">PREMIUM FACILITIES</div>
            <h2 className="display-5 fw-bold">World Class Amenities</h2>
            <p className="text-white-50 mx-auto mt-3" style={{ maxWidth: "600px" }}>
              Every property is equipped with luxury amenities to ensure an exceptional stay.
            </p>
          </div>

          <div className="row g-4">
            {amenities.map((item, i) => (
              <div className="col-sm-6 col-lg-3" key={i}>
                <div className="card bg-white bg-opacity-5 border-white border-opacity-10 rounded-4 text-center h-100">
                  <div className="card-body p-4">
                    <div className="display-3 text-danger mb-3">{item.icon}</div>
                    <h5 className="fw-bold">{item.title}</h5>
                    <p className="text-white-50 small">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS SECTION ========== */}
      <section id="testimonials" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <div className="text-danger small tracking-wide mb-2">HAPPY CLIENTS</div>
            <h2 className="display-5 fw-bold">Guest Experiences</h2>
          </div>

          <div className="row g-4">
            {testimonials.map((testimonial, i) => (
              <div className="col-md-4" key={i}>
                <div className="card bg-white bg-opacity-5 border-white border-opacity-10 rounded-4 h-100">
                  <div className="card-body p-4">
                    <div className="text-warning mb-3">
                      {[...Array(5)].map((_, idx) => (
                        <FaStar key={idx} className="me-1" />
                      ))}
                      <span className="ms-2 text-white">{testimonial.rating}</span>
                    </div>
                    <p className="text-white-80 fst-italic mb-4">"{testimonial.text}"</p>
                    <div>
                      <h6 className="fw-bold mb-0">{testimonial.name}</h6>
                      <small className="text-danger">{testimonial.role}</small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CONTACT SECTION ========== */}
      <section id="contact" className="py-5 position-relative">
        <div className="container">
          <div className="row g-5">
            {/* Contact Info */}
            <div className="col-lg-6">
              <div className="mb-4">
                <div className="text-danger small tracking-wide mb-2">GET IN TOUCH</div>
                <h2 className="display-5 fw-bold">Plan Your Perfect Escape</h2>
                <p className="text-white-50 mt-3">
                  Our team is ready to help you create unforgettable memories. Reach out for personalized assistance.
                </p>
              </div>

              <div className="d-flex flex-column gap-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-danger bg-opacity-25 p-3 rounded-3">
                    <FaPhone className="text-danger" size={20} />
                  </div>
                  <div>
                    <small className="text-white-50">Call Us</small>
                    <p className="fw-bold mb-0">+91 98765 43210</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-danger bg-opacity-25 p-3 rounded-3">
                    <FaWhatsapp className="text-danger" size={20} />
                  </div>
                  <div>
                    <small className="text-white-50">WhatsApp</small>
                    <p className="fw-bold mb-0">+91 98765 43210</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-danger bg-opacity-25 p-3 rounded-3">
                    <FaEnvelope className="text-danger" size={20} />
                  </div>
                  <div>
                    <small className="text-white-50">Email</small>
                    <p className="fw-bold mb-0">bookings@vfarmhouse.com</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-danger bg-opacity-25 p-3 rounded-3">
                    <FaMapMarkerAlt className="text-danger" size={20} />
                  </div>
                  <div>
                    <small className="text-white-50">Head Office</small>
                    <p className="fw-bold mb-0">Lodhi Road, New Delhi</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-6">
              <div className="card bg-white bg-opacity-5 border-white border-opacity-10 rounded-4 p-4">
                <h3 className="h4 fw-bold mb-4">Send us a message</h3>
                <form>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <input type="text" className="form-control bg-white bg-opacity-10 border-white border-opacity-10 text-white" placeholder="Your Name" />
                    </div>
                    <div className="col-md-6">
                      <input type="tel" className="form-control bg-white bg-opacity-10 border-white border-opacity-10 text-white" placeholder="Phone Number" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <input type="email" className="form-control bg-white bg-opacity-10 border-white border-opacity-10 text-white" placeholder="Email Address" />
                  </div>
                  <div className="mb-3">
                    <select className="form-select bg-white bg-opacity-10 border-white border-opacity-10 text-white">
                      <option>Select Property Type</option>
                      <option>Luxury Villa</option>
                      <option>Farmhouse</option>
                      <option>Mountain Resort</option>
                      <option>Lake House</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <textarea rows="4" className="form-control bg-white bg-opacity-10 border-white border-opacity-10 text-white" placeholder="Tell us about your celebration..."></textarea>
                  </div>
                  <button type="submit" className="btn btn-danger w-100 rounded-pill py-3 fw-semibold">
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="border-top border-white border-opacity-10 py-5">
        <div className="container">
          <div className="row g-4">
            <div className="col-sm-6 col-lg-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                  <span className="fw-bold">V</span>
                </div>
                <h5 className="fw-bold mb-0">V Farm House</h5>
              </div>
              <p className="text-white-50 small">Creating luxury experiences in nature's embrace since 2010.</p>
            </div>
            <div className="col-sm-6 col-lg-3">
              <h6 className="fw-bold mb-3">Quick Links</h6>
              <ul className="list-unstyled">
                {["Home", "Properties", "Amenities", "Gallery", "About", "Contact"].map((item) => (
                  <li key={item} className="mb-2">
                    <a href="#" className="text-white-50 text-decoration-none hover-text-danger small">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-sm-6 col-lg-3">
              <h6 className="fw-bold mb-3">Properties</h6>
              <ul className="list-unstyled">
                {["Luxury Villas", "Farm Houses", "Mountain Resorts", "Lake Houses", "Beach Properties", "Party Venues"].map((item) => (
                  <li key={item} className="mb-2">
                    <a href="#" className="text-white-50 text-decoration-none hover-text-danger small">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-sm-6 col-lg-3">
              <h6 className="fw-bold mb-3">Newsletter</h6>
              <p className="text-white-50 small mb-3">Subscribe for exclusive offers and updates.</p>
              <div className="input-group">
                <input type="email" className="form-control bg-white bg-opacity-10 border-white border-opacity-10 text-white" placeholder="Your email" />
                <button className="btn btn-danger">→</button>
              </div>
            </div>
          </div>
          <hr className="border-white border-opacity-10 my-4" />
          <div className="text-center text-white-50 small">
            <p className="mb-0">© 2026 V Farm House — Luxury Nature Retreats. All rights reserved.</p>
            <p className="mt-1">Designed with ❤️ for exceptional experiences</p>
          </div>
        </div>
      </footer>

      {/* ========== FLOATING WHATSAPP BUTTON ========== */}
      <a
        href="https://wa.me/919876543210"
        target="_blank"
        rel="noopener noreferrer"
        className="position-fixed bottom-0 end-0 m-4 z-3"
        style={{ bottom: "20px", right: "20px" }}
      >
        <div className="bg-danger rounded-circle d-flex align-items-center justify-content-center shadow-lg" style={{ width: "55px", height: "55px" }}>
          <FaWhatsapp size={28} className="text-white" />
        </div>
      </a>

      {/* ========== SCROLL TO TOP BUTTON ========== */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="position-fixed bottom-0 end-0 m-4 bg-danger border-0 rounded-circle d-flex align-items-center justify-content-center shadow-lg"
          style={{ width: "45px", height: "45px", bottom: "90px", right: "20px", zIndex: 1000 }}
        >
          <FaArrowUp className="text-white" />
        </button>
      )}

      {/* Scroll Progress Bar */}
      <div
        className="position-fixed top-0 start-0 bg-danger"
        style={{ width: `${scrollProgress}%`, height: "3px", zIndex: 9999 }}
      />

      {/* Custom CSS Styles */}
      <style jsx>{`
        .text-gradient {
          background: linear-gradient(135deg, #FF5A5F, #FF7E63);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .tracking-wide {
          letter-spacing: 2px;
        }
        .hover-text-danger:hover {
          color: #FF5A5F !important;
        }
        .bg-opacity-5 {
          --bs-bg-opacity: 0.05;
        }
        .bg-opacity-10 {
          --bs-bg-opacity: 0.1;
        }
        .bg-opacity-25 {
          --bs-bg-opacity: 0.25;
        }
        .border-opacity-10 {
          --bs-border-opacity: 0.1;
        }
        .text-white-80 {
          color: rgba(255, 255, 255, 0.8);
        }
        .min-vh-100 {
          min-height: 100vh;
        }
        .z-1 {
          z-index: 1;
        }
        .z-3 {
          z-index: 3;
        }
      `}</style>
    </div>
  );
}